import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { addMessage, setSelectedUseCase } from "../../ChatSlice_Store";
import { setLayoutMode } from "../../LayoutSlice_Store";
import { openDocumentPreview } from "../../DocumentPreviewSlice_Store";
import {
  buildDocumentPreviewPayload,
  buildHighlightRequest,
} from "../../services/factPreviewService";
import { canPreviewDocument } from "../../services/documentPreviewService";

export interface UseCaseNotice {
  useCaseSelected: boolean;
  selectedUseCase: string | null;
  limitationsDismissed: boolean;
  dismissLimitations: () => void;
}

/** Selected use case plus the BETA limitations bubble's dismissal state. */
export function useUseCaseNotice(): UseCaseNotice {
  const useCaseSelected = useAppSelector((state) => state.chatSlice.useCaseSelected);
  const selectedUseCase = useAppSelector((state) => state.chatSlice.selectedUseCase);

  const [limitationsDismissed, setLimitationsDismissed] = useState(false);
  const prevSelectedUseCaseRef = useRef<string | null>(null);

  // Reset limitations bubble when use case changes
  useEffect(() => {
    if (selectedUseCase !== prevSelectedUseCaseRef.current) {
      setLimitationsDismissed(false);
      prevSelectedUseCaseRef.current = selectedUseCase;
    }
  }, [selectedUseCase]);

  return {
    useCaseSelected,
    selectedUseCase,
    limitationsDismissed,
    dismissLimitations: () => setLimitationsDismissed(true),
  };
}

/**
 * Opens the PDF viewer for a source document, highlighting every fact this
 * conversation has collected for it.
 */
export function useDocumentOpener(allFacts: any[]): (document: any) => void {
  const dispatch = useAppDispatch();

  return (document: any): void => {
    if (!document || !document.documentId) {
      console.warn("Cannot open document: missing document or documentId");
      return;
    }

    const documentToUse = buildDocumentPreviewPayload(document, null);

    if (!canPreviewDocument(documentToUse)) {
      console.warn("Document cannot be previewed:", documentToUse);
      return;
    }

    // Gather ALL facts for this document from the central store
    const documentFacts = allFacts.filter((f: any) => f.documentId === document.documentId);

    if (documentFacts.length === 0) {
      console.warn("No facts found for document:", document.documentId);
      // Still open the document even if no facts are found
    }

    // No specific fact is preferred when clicking a source button
    const highlightRequest = buildHighlightRequest(
      { documentId: document.documentId, facts: documentFacts },
      null
    );

    if (highlightRequest) {
      highlightRequest.allDocumentFacts = documentFacts;
    }

    // Switch to workspace mode so the preview panel is visible
    dispatch(setLayoutMode("workspace"));
    dispatch(openDocumentPreview({ document: documentToUse, highlightRequest }));
  };
}

/** Records a conversation-starter pick as a local bot message. */
export function useStarterSelection(): (followUpPrompt: string, useCaseId: string) => void {
  const dispatch = useAppDispatch();

  return (followUpPrompt: string, useCaseId: string): void => {
    if (!followUpPrompt) {
      return;
    }

    // Set the specific use case ID (e.g., 'past-deals', 'research-desk')
    dispatch(setSelectedUseCase(useCaseId));

    console.log("💡 Conversation starter selected:", useCaseId, followUpPrompt);

    // Add the starter prompt as a bot message (not sent to core-agent)
    dispatch(addMessage({ sender: "chatbot", text: followUpPrompt }));
  };
}
